import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  Alert,
  useWindowDimensions,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import profileDetailStyles from '../theme/profileDetailStyles';
import api from '../services/api';
import {
  FAMILY_TYPE_OPTIONS,
  FAMILY_STATUS_OPTIONS,
  FAMILY_VALUES_OPTIONS,
  SURVIVAL_STATUS_OPTIONS,
} from '../constants/profileOptions';

const FamilyTabScreen = ({ memberId, isOwnProfile, isMediatorProfile, onEditStateChange }) => {
  const { width, height } = useWindowDimensions();
  const isLandscape = width > height;
  const scrollViewRef = useRef(null);
  const fieldPositions = useRef({});
  
  const [familyData, setFamilyData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [activeDropdownField, setActiveDropdownField] = useState(null);
  const [activeDropdownOptions, setActiveDropdownOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadFamilyData();
  }, [memberId]);

  useEffect(() => {
    if (isEditMode && onEditStateChange && Object.keys(editData).length > 0) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: true,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  }, [editData, isEditMode]);

  const loadFamilyData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/EnhancedMember/${memberId}/family`);
      const data = response.data?.data || response.data;
      // Always set data even if null/empty to allow editing
      setFamilyData(data || {});
    } catch (error) {
      console.error('Error loading family data:', error);
      // Set empty object to allow editing even on error
      setFamilyData({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    const family = familyData?.familyDetails || familyData || {};
    setEditData(family);
    setIsEditMode(true);
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: false,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData({});
    setValidationErrors({});
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: false,
        hasUnsavedChanges: false,
        onSave: null,
        onCancel: null,
      });
    }
  };

  const getMandatoryFields = () => [
    { key: 'familyType', label: 'Family Type' },
    { key: 'familyStatus', label: 'Family Status' },
    { key: 'familyValues', label: 'Family Values' },
    { key: 'fathersFullName', label: "Father's Full Name" },
    { key: 'mothersFullName', label: "Mother's Full Name" },
  ];

  const handleSave = async () => {
    // Validate mandatory fields
    const mandatoryFields = getMandatoryFields();
    const errors = {};

    mandatoryFields.forEach(field => {
      const value = field.key in editData ? editData[field.key] : (familyData?.familyDetails?.[field.key] || familyData?.[field.key]);
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors[field.key] = `${field.label} is required`;
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Scroll to first error field
      if (scrollViewRef.current) {
        const firstErrorKey = Object.keys(errors)[0];
        const errorPosition = fieldPositions.current[firstErrorKey];
        if (errorPosition !== undefined) {
          scrollViewRef.current.scrollTo({ y: Math.max(0, errorPosition - 100), animated: true });
        } else {
          scrollViewRef.current.scrollTo({ y: 0, animated: true });
        }
      }
      
      Alert.alert('Mandatory Fields Required', 'Please fill in all mandatory fields');
      return false;
    }

    setIsSaving(true);
    try {
      const response = await api.put(`/api/EnhancedMember/${memberId}/family`, editData);
      setFamilyData(response.data?.data || response.data);
      Alert.alert('Success', 'Family details updated successfully');
      setIsEditMode(false);
      setEditData({});
      setValidationErrors({});
      if (onEditStateChange) {
        onEditStateChange({
          isEditing: false,
          hasUnsavedChanges: false,
          onSave: null,
          onCancel: null,
        });
      }
      return true;
    } catch (error) {
      console.error('Error saving family data:', error);
      Alert.alert('Error', error.response?.data?.message || 'Failed to save changes');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const getOptionsForField = (fieldKey) => {
    const optionsMap = {
      familyType: FAMILY_TYPE_OPTIONS,
      familyStatus: FAMILY_STATUS_OPTIONS,
      familyValues: FAMILY_VALUES_OPTIONS,
      survivalStatusOfFather: SURVIVAL_STATUS_OPTIONS,
      survivalStatusOfMother: SURVIVAL_STATUS_OPTIONS,
    };
    return optionsMap[fieldKey] || null;
  };

  const openDropdown = (fieldKey, label) => {
    const options = getOptionsForField(fieldKey);
    if (options) {
      setActiveDropdownField({ key: fieldKey, label });
      setActiveDropdownOptions(options);
      setDropdownVisible(true);
    }
  };

  const selectDropdownOption = (value) => {
    if (activeDropdownField) {
      setEditData({ ...editData, [activeDropdownField.key]: value });
      // Clear error when user selects a value
      if (validationErrors[activeDropdownField.key]) {
        setValidationErrors({ ...validationErrors, [activeDropdownField.key]: undefined });
      }
    }
    setDropdownVisible(false);
    setActiveDropdownField(null);
  };

  const renderSectionTitle = (title) => (
    <Text style={profileDetailStyles.sectionHeader}>{title}</Text>
  );

  const renderInfoRow = (label, value, fieldKey, editable = true) => {
    const isEditing = isEditMode && editable;
    const options = getOptionsForField(fieldKey);
    const hasDropdown = options !== null;
    const isMandatory = getMandatoryFields().some(f => f.key === fieldKey);
    const hasError = validationErrors[fieldKey];
    const displayLabel = isEditing && isMandatory ? `${label} *` : label;
    
    if (isEditing) {
      // Edit mode with floating label
      return (
        <View 
          style={profileDetailStyles.inputWrapper}
          onLayout={(event) => {
            fieldPositions.current[fieldKey] = event.nativeEvent.layout.y;
          }}
        >
          <Text style={profileDetailStyles.floatingLabel}>{displayLabel}</Text>
          {hasDropdown ? (
            <TouchableOpacity
              style={[profileDetailStyles.dropdown, hasError && profileDetailStyles.inputError]}
              onPress={() => openDropdown(fieldKey, label)}
            >
              <Text style={(fieldKey in editData ? editData[fieldKey] : value) ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
                {fieldKey in editData ? (editData[fieldKey] || `Select ${label}`) : (value || `Select ${label}`)}
              </Text>
              <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          ) : (
            <TextInput
              style={[
                profileDetailStyles.input,
                focusedInput === fieldKey && profileDetailStyles.inputFocused,
                hasError && profileDetailStyles.inputError
              ]}
              value={fieldKey in editData ? (editData[fieldKey]?.toString() || '') : (value?.toString() || '')}
              onChangeText={(text) => {
                setEditData({ ...editData, [fieldKey]: text });
                if (hasError) {
                  setValidationErrors({ ...validationErrors, [fieldKey]: undefined });
                }
              }}
              onFocus={() => setFocusedInput(fieldKey)}
              onBlur={() => setFocusedInput(null)}
              placeholder={`Enter ${label}`}
              placeholderTextColor={colors.textSecondary}
            />
          )}
          {hasError && (
            <Text style={profileDetailStyles.errorText}>{hasError}</Text>
          )}
        </View>
      );
    } else {
      // Display mode
      return (
        <View style={profileDetailStyles.infoItem}>
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>{label}</Text>
            <View style={profileDetailStyles.viewModeContainer}>
              <Text style={profileDetailStyles.viewModeText}>{value || 'Not Provided'}</Text>
            </View>
          </View>
        </View>
      );
    }
  };

  if (isLoading) {
    return (
      <View style={profileDetailStyles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.secondary} />
        <Text style={profileDetailStyles.loadingText}>Loading family details...</Text>
      </View>
    );
  }

  const family = familyData?.familyDetails || familyData || {};

  return (
    <View style={profileDetailStyles.container}>
      <ScrollView 
        ref={scrollViewRef}
        style={profileDetailStyles.scrollView} 
        contentContainerStyle={profileDetailStyles.scrollContent}
      >
        {renderSectionTitle('Basic Family Information')}
        {renderInfoRow('Family Type', family.familyType, 'familyType')}
        {renderInfoRow('Family Status', family.familyStatus, 'familyStatus')}
        {renderInfoRow('Family Values', family.familyValues, 'familyValues')}
        {renderInfoRow('Assets', family.assets, 'assets')}
        
        {renderSectionTitle('Parents Information')}
        {renderInfoRow("Father's Full Name", family.fathersFullName, 'fathersFullName')}
        {renderInfoRow("Mother's Full Name", family.mothersFullName, 'mothersFullName')}
        {renderInfoRow("Mother's Maiden Name", family.mothersMaidenName, 'mothersMaidenName')}
        {renderInfoRow("Father's Occupation", family.fathersOccupation, 'fathersOccupation')}
        {renderInfoRow("Mother's Occupation", family.mothersOccupation, 'mothersOccupation')}
        {renderInfoRow('Survival Status of Father', family.survivalStatusOfFather, 'survivalStatusOfFather')}
        {renderInfoRow('Survival Status of Mother', family.survivalStatusOfMother, 'survivalStatusOfMother')}

        {renderSectionTitle('Siblings Information')}
        {renderInfoRow('Number of Siblings', family.numberOfSiblings, 'numberOfSiblings')}
        {renderInfoRow('Number of Brothers', family.numberOfBrothers, 'numberOfBrothers')}
        {renderInfoRow('Number of Married Brothers', family.numberOfMarriedBrothers, 'numberOfMarriedBrothers')}
        {renderInfoRow('Number of Sisters', family.numberOfSisters, 'numberOfSisters')}
        {renderInfoRow('Number of Married Sisters', family.numberOfMarriedSisters, 'numberOfMarriedSisters')}
      </ScrollView>

      {/* Floating Edit Buttons */}
      {(isOwnProfile || isMediatorProfile) && (
        <View style={profileDetailStyles.floatingButtonContainer}>
          {isEditMode ? (
            <>
              <TouchableOpacity 
                onPress={handleCancelEdit} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingCancelButton]}
                disabled={isSaving}
              >
                <SvgIcon name="close" size={20} color={colors.white} />
              </TouchableOpacity>
              <TouchableOpacity 
                onPress={handleSave} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingSaveButton]}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <SvgIcon name="checkmark" size={20} color={colors.white} />
                )}
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity 
              onPress={handleEdit} 
              style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingEditButton]}
            >
              <SvgIcon name="create-outline" size={20} color={colors.white} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Dropdown Modal */}
      <Modal
        visible={dropdownVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setDropdownVisible(false)}
      >
        <TouchableOpacity 
          style={profileDetailStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          <View style={profileDetailStyles.modalContent}>
            <View style={profileDetailStyles.modalHeader}>
              <Text style={profileDetailStyles.modalTitle}>
                {activeDropdownField?.label || 'Select'}
              </Text>
              <TouchableOpacity onPress={() => setDropdownVisible(false)}>
                <SvgIcon name="close" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
            <ScrollView style={profileDetailStyles.modalScroll}>
              {activeDropdownOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={profileDetailStyles.modalItem}
                  onPress={() => selectDropdownOption(option.value)}
                >
                  <Text style={profileDetailStyles.modalItemText}>{option.label}</Text>
                  {(editData[activeDropdownField?.key] || family[activeDropdownField?.key]) === option.value && (
                    <SvgIcon name="checkmark-circle" size={20} color={colors.secondary} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default FamilyTabScreen;
